import { NotificationService, type NotificationResponse } from '@acme/api';
import { Icon } from '@acme/assets';
import { PortalDropdown, usePortalDropdown } from '@acme/foundation';
import { Badge } from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const DropdownLink = ({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) => {
  const { close } = usePortalDropdown();

  return (
    <Link
      to={to}
      onClick={close}
      className="flex items-center justify-center gap-2 text-sm text-gray-700 hover:text-brand-primary-default"
    >
      {children}
    </Link>
  );
};

function formatNotificationDate(dateString?: string | null) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })}, ${date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export const NotificationsControl = ({
  notificationsLinkPath,
}: {
  notificationsLinkPath: string;
}) => {
  const [items, setItems] = useState<NotificationResponse[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res =
        await NotificationService.getApiV1NotificationNotificationUnreadCount();
      if (res?.data != null) setUnreadCount(res.data);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        NotificationService.getApiV1NotificationNotification({
          isRead: false,
          pageNumber: 1,
          pageSize: 5,
        }),
        NotificationService.getApiV1NotificationNotificationUnreadCount(),
      ]);
      setItems(listRes?.data?.items ?? []);
      if (countRes?.data != null) setUnreadCount(countRes.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) loadList();
    },
    [loadList]
  );

  return (
    <PortalDropdown
      placement="bottom-end"
      dismissOnClick={false}
      onOpenChange={handleOpenChange}
      trigger={
        <div className="relative cursor-pointer p-2">
          <Icon name="bell-outline" size={24} alt="Уведомления" />
          {unreadCount > 0 && (
            <Badge
              color="green"
              size="xs"
              className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full"
            >
              {unreadCount}
            </Badge>
          )}
        </div>
      }
    >
      <div className="flex flex-col w-[400px] max-h-[500px] overflow-y-auto bg-white">
        <div className="px-4 py-3 bg-gray-50">
          <h3 className="font-semibold text-base text-gray-900 text-center">
            Уведомления
          </h3>
        </div>

        <div className="flex flex-col">
          {loading ? (
            <div className="px-4 py-8 text-center text-gray-500 text-sm">
              Загрузка…
            </div>
          ) : items.length > 0 ? (
            items.map((n) => (
              <div
                key={n.id ?? ''}
                className={`px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 `}
              >
                <div className="flex flex-col gap-1">
                  {n.applicationCode && (
                    <div className="text-sm text-gray-700 leading-4">
                      <Link
                        to={`/application/${n.applicationId}`}
                        className="text-sm hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {n.applicationCode
                          ? `Заявка: ${n.applicationCode}`
                          : n.subject || n.description}
                      </Link>
                    </div>
                  )}
                  {n.description && n.subject !== n.description && (
                    <p className="text-sm text-gray-700">{n.description}</p>
                  )}

                  <span className="text-xs text-blue-600">
                    {formatNotificationDate(n.createdAt)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-gray-500 text-sm">
              Все уведомления прочитаны
            </div>
          )}
        </div>

        <div className="px-4 py-3 bg-gray-50">
          <DropdownLink to={notificationsLinkPath}>
            <Icon name="eye" size={20} alt="Посмотреть все" />
            <span>Посмотреть все</span>
          </DropdownLink>
        </div>
      </div>
    </PortalDropdown>
  );
};
